import { Loader2Icon } from 'lucide-react';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { ListPagination } from '@/components/list-pagination';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FAQ_CATEGORY_SLUG, useAppPaths } from '@/products/helpdesk/features/portal/portal-paths';
import { useKbArticles, useKbLookups, useKbSearch } from '@/products/helpdesk/features/kb/use-kb';
import { ApiRequestError } from '@/lib/api';

const ALL = 'all';

export function PortalKbPage() {
  const copy = useVoiceCopy();
  const paths = useAppPaths();
  const lookups = useKbLookups();
  const [params, setParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(params.get('q') ?? '');
  const [q, setQ] = useState(params.get('q') ?? '');
  const [page, setPage] = useState(1);
  const requestedCategory = params.get('kategorie') ?? ALL;
  const [pageCategory, setPageCategory] = useState(requestedCategory);
  if (pageCategory !== requestedCategory) {
    setPageCategory(requestedCategory);
    setPage(1);
  }

  const categories = useMemo(
    () => (lookups.data?.categories ?? []).filter((category) => category.isActive),
    [lookups.data?.categories],
  );
  const categoryId =
    requestedCategory === ALL
      ? undefined
      : categories.find(
          (category) => category.slug === requestedCategory || category.id === requestedCategory,
        )?.id;

  const list = useKbArticles({
    page,
    pageSize: 25,
    sortBy: 'title',
    sortOrder: 'asc',
    categoryId,
  });
  const search = useKbSearch(q.trim() ? { q: q.trim(), limit: 8, categoryId } : null);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = searchInput.trim();
      setQ(next);
      setParams(
        (current) => {
          const copy = new URLSearchParams(current);
          if (next) {
            copy.set('q', next);
          } else {
            copy.delete('q');
          }
          return copy;
        },
        { replace: true },
      );
    }, 400);
    return () => window.clearTimeout(handle);
  }, [searchInput, setParams]);

  function selectCategory(slug: string) {
    setParams((current) => {
      const copy = new URLSearchParams(current);
      if (slug === ALL) {
        copy.delete('kategorie');
      } else {
        copy.set('kategorie', slug);
      }
      return copy;
    });
  }

  const browseCategories = categories.filter((category) => category.slug !== FAQ_CATEGORY_SLUG);
  const activeSlug =
    categories.find((category) => category.id === categoryId)?.slug ??
    (requestedCategory === ALL ? ALL : requestedCategory);

  return (
    <section className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Hilfe finden</h1>
        <p className="mt-1 text-sm text-muted-foreground">{copy('helpdesk.portal.kb.lead')}</p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="portal-kb-search">{copy('helpdesk.portal.kb.search')}</Label>
        <Input
          id="portal-kb-search"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="z. B. VPN, Passwort, Drucker …"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <TopicChip
          label="Alle Themen"
          active={activeSlug === ALL}
          onClick={() => selectCategory(ALL)}
        />
        {browseCategories.map((category) => (
          <TopicChip
            key={category.id}
            label={category.name}
            active={activeSlug === category.slug}
            onClick={() => selectCategory(category.slug)}
          />
        ))}
        <TopicChip
          label="Häufige Fragen"
          active={activeSlug === FAQ_CATEGORY_SLUG}
          onClick={() => selectCategory(FAQ_CATEGORY_SLUG)}
        />
      </div>

      {q && (
        <Card>
          <CardHeader>
            <CardTitle>Suchergebnisse</CardTitle>
            <CardDescription>
              {search.isPending
                ? 'Wir suchen …'
                : search.data
                  ? search.data.items.length === 0
                    ? copy('helpdesk.portal.kb.no-hit')
                    : `${search.data.items.length} Treffer`
                  : null}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {search.isError && (
              <p className="text-sm text-destructive">
                {search.error instanceof ApiRequestError
                  ? search.error.message
                  : 'Die Suche ist gerade nicht erreichbar.'}
              </p>
            )}
            {search.data?.items.map((hit) => (
              <Link
                key={hit.article.id}
                to={paths.article(hit.article.slug)}
                className="block rounded-md border p-3 hover:bg-muted/40"
              >
                <p className="font-medium">{hit.article.title}</p>
                {hit.snippet && <p className="mt-1 text-sm text-muted-foreground">{hit.snippet}</p>}
              </Link>
            ))}
          </CardContent>
        </Card>
      )}

      {list.isPending && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Anleitungen werden geladen …
        </p>
      )}
      {list.data && !q && (
        <ul className="grid gap-3 sm:grid-cols-2">
          {list.data.items.map((article) => (
            <li key={article.id}>
              <Link
                to={paths.article(article.slug)}
                className="block h-full rounded-md border bg-card p-4 hover:bg-muted/40"
              >
                <p className="font-medium">{article.title}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {article.summary ?? article.category?.name ?? 'Anleitung'}
                </p>
              </Link>
            </li>
          ))}
          {list.data.items.length === 0 && (
            <li className="col-span-full text-sm text-muted-foreground">
              In diesem Thema gibt es noch keine öffentlichen Anleitungen.
            </li>
          )}
        </ul>
      )}
      {list.data && !q ? (
        <ListPagination
          page={list.data.page}
          pageSize={list.data.pageSize}
          total={list.data.total}
          onPageChange={setPage}
        />
      ) : null}
    </section>
  );
}

function TopicChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? 'rounded-full bg-primary px-3 py-1.5 text-sm text-primary-foreground'
          : 'rounded-full bg-muted px-3 py-1.5 text-sm text-foreground hover:bg-muted/80'
      }
    >
      {label}
    </button>
  );
}
